import { filterDateFieldClass, filterDateInputClass } from './uiClasses.js';

/**
 * Shared settlement filter card: status/view pills on the left, dates (and extras) on the right.
 * singleRow keeps hub, agent, dates, and status on one line. The status track
 * fills the space left in that row. Other pages keep the stacked mobile / row desktop layout.
 */
export default function SettlementFilterBar({
  statusLabel = 'Status',
  tabs = [],
  activeTab,
  onTabChange,
  counts = {},
  fromDate = '',
  toDate = '',
  onFromDate,
  onToDate,
  extraLeft = null,
  extraRight = null,
  showDates = true,
  centerStatus = false,
  singleRow = false,
  children = null,
}) {
  const statusTabs = tabs.length > 0 ? (
    <div className={singleRow
      ? 'min-w-0 w-full flex-1 xl:w-auto'
      : centerStatus
        ? 'flex-1 min-w-0 lg:flex lg:flex-col lg:items-center'
        : 'flex-1 min-w-0'}>
      <label className={singleRow
        ? 'block text-xs font-medium text-gray-500 mb-1.5'
        : 'block text-xs font-medium text-gray-600 mb-1'}>{statusLabel}</label>
      <div className={singleRow
        ? 'flex h-[42px] w-full min-w-0 flex-nowrap items-center gap-1 overflow-x-auto rounded-xl border border-gray-200 bg-gray-50 px-1'
        : 'flex flex-wrap gap-1.5 p-1 rounded-xl bg-gray-50 border border-gray-200'}>
        {tabs.map((tab) => {
          const active = activeTab === tab.id;
          const count = tab.count ?? counts[tab.id];
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange?.(tab.id)}
              className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-lg font-semibold transition-colors ${
                singleRow ? 'h-8 gap-1.5 px-3 text-sm' : 'gap-1.5 px-3 py-1.5 text-xs sm:text-sm'
              } ${
                active
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
              }`}
            >
              {tab.label}
              {count != null ? (
                <span
                  className={`min-w-[1.25rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                    active ? 'bg-blue-50 text-blue-700' : 'bg-gray-200/80 text-gray-600'
                  }`}
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  ) : (
    singleRow ? null : <div className="flex-1 min-w-0" />
  );

  const dateFields = (
    <div className={singleRow
      ? 'flex w-full min-w-0 shrink-0 flex-wrap items-end gap-3 sm:w-auto xl:w-auto'
      : 'grid grid-cols-2 gap-2 sm:gap-3 w-full min-w-0 items-end lg:flex lg:flex-wrap lg:w-auto lg:shrink-0'}>
      {showDates ? (
        <>
          <div className={singleRow ? 'w-[calc(50%-0.375rem)] min-w-0 sm:w-40' : filterDateFieldClass}>
            <label className={singleRow
              ? 'block text-xs font-medium text-gray-500 mb-1.5'
              : 'block text-xs font-medium text-gray-600 mb-1'}>From date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => onFromDate?.(e.target.value)}
              className={filterDateInputClass}
            />
          </div>
          <div className={singleRow ? 'w-[calc(50%-0.375rem)] min-w-0 sm:w-40' : filterDateFieldClass}>
            <label className={singleRow
              ? 'block text-xs font-medium text-gray-500 mb-1.5'
              : 'block text-xs font-medium text-gray-600 mb-1'}>To date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => onToDate?.(e.target.value)}
              className={filterDateInputClass}
            />
          </div>
        </>
      ) : null}
      {extraRight ? (
        <div className={singleRow ? 'shrink-0' : 'col-span-2 lg:col-auto min-w-0 w-full lg:w-auto'}>
          {extraRight}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 mb-4">
      {singleRow ? (
        <div className="flex w-full min-w-0 flex-wrap items-end gap-3 xl:flex-nowrap">
          {extraLeft}
          {dateFields}
          {statusTabs}
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          {extraLeft}
          {statusTabs}
          {dateFields}
        </div>
      )}
      {children}
    </div>
  );
}
